/**
 * ImprovementDetail — slide-in detail panel for a single improvement proposal.
 * Rendered inside the Improvements page as a side-panel; full detail is loaded
 * via a separate query so the list stays fast.
 */
import type React from 'react';
import { useQuery } from '@tanstack/react-query';
import { ExternalLink, CheckCircle2, ChevronRight, ClipboardCheck } from 'lucide-react';
import { getImprovementProposalOptions } from '../../generated/api/@tanstack/react-query.gen.js';
import { Badge, Callout, Sheet, Skeleton } from '../../components/ui/index.js';
import type { ImprovementProposal } from '../../generated/api/types.gen.js';
import { ProposalDecisionActions } from './ProposalDecisionActions.js';

type ImprovementEvidenceRef = ImprovementProposal['evidenceRefs'][number];
type ImprovementStageLogEntry = ImprovementProposal['stageLog'][number];
type AcceptanceCriterion = NonNullable<ImprovementProposal['acceptanceCriteria']>[number];
import { safeHref } from '../../lib/safe-url.js';

// ── Sub-components ─────────────────────────────────────────────────────────────

function EvidenceRefRow({ ref: evRef }: { ref: ImprovementEvidenceRef }) {
  const label = evRef.note ?? evRef.id ?? evRef.url ?? evRef.type;
  if (evRef.url) {
    return (
      <a
        href={safeHref(evRef.url) ?? undefined}
        target="_blank"
        rel="noopener noreferrer"
        className="flex items-center gap-1 text-accent-text hover:underline text-xs"
      >
        <ExternalLink size={11} />
        {label}
      </a>
    );
  }
  return (
    <span className="text-xs text-muted">
      <span className="text-faint">[{evRef.type}]</span> {label}
    </span>
  );
}

function CriterionRow({ criterion }: { criterion: AcceptanceCriterion }) {
  return (
    <div className="flex items-start gap-2 py-1">
      <CheckCircle2 size={14} className={`mt-0.5 shrink-0 ${criterion.blocking ? 'text-warning' : 'text-success'}`} />
      <span className="text-xs text-body flex-1">{criterion.description}</span>
      {criterion.blocking && (
        <Badge variant="warning" size="sm">
          blocking
        </Badge>
      )}
    </div>
  );
}

function StageLogTimeline({ entries }: { entries: ImprovementStageLogEntry[] }) {
  const sorted = [...entries].sort((a, b) => new Date(a.at).getTime() - new Date(b.at).getTime());
  return (
    <ol className="relative border-l border ml-2 space-y-3 pl-4">
      {sorted.map((entry, i) => (
        <li key={`${entry.at}-${i}`} className="relative">
          <span className="absolute left-[-1.15rem] top-0.5 flex h-3 w-3 items-center justify-center rounded-full bg-surface-alt border" />
          <div className="text-xs">
            <span className="text-muted">{entry.from}</span>
            <ChevronRight size={10} className="inline mx-1 text-faint" />
            <span className="font-medium text-body">{entry.to}</span>
            <span className="ml-2 text-faint">{new Date(entry.at).toLocaleString()}</span>
          </div>
          {entry.note && <p className="mt-0.5 text-xs text-muted">{entry.note}</p>}
          <p className="text-xs text-faint">
            {entry.actorType} · {entry.actorId}
          </p>
        </li>
      ))}
    </ol>
  );
}

// ── Main detail panel ──────────────────────────────────────────────────────────

interface ImprovementDetailProps {
  proposalId: number;
  onClose: () => void;
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="space-y-2">
      <h3 className="text-xs font-semibold uppercase tracking-wide text-muted">{title}</h3>
      {children}
    </div>
  );
}

export function ImprovementDetail({ proposalId, onClose }: ImprovementDetailProps) {
  const { data: proposal, isLoading, error } = useQuery(getImprovementProposalOptions({ path: { id: proposalId } }));

  const needsDesignReview = proposal?.status === 'specced' && proposal.hasOpenDesignReview;
  const needsFinalDecision = proposal?.status === 'ready_for_human';

  return (
    <Sheet
      open
      onClose={onClose}
      width="xl"
      title={isLoading ? 'Loading…' : (proposal?.title ?? 'Proposal detail')}
      description={proposal ? `#${proposal.id}` : undefined}
      footer={
        needsDesignReview ? (
          <div className="w-full space-y-3">
            <div className="flex items-center gap-2">
              <ClipboardCheck size={14} className="shrink-0 text-warning" />
              <span className="text-xs font-semibold text-heading">Design review</span>
              <Badge variant="warning" size="sm">
                early gate
              </Badge>
            </div>
            <p className="text-xs text-muted">
              <strong className="text-body">Approving the design</strong> lets the AI proceed to implementation and
              audit. A <strong className="text-body">separate final decision</strong> will be required once
              implementation is complete.
            </p>
            <ProposalDecisionActions proposalId={proposalId} gate="design" onDecided={onClose} />
          </div>
        ) : needsFinalDecision ? (
          <div className="w-full space-y-3">
            <p className="text-xs text-muted">
              Final decision. This is irreversible. A rationale is required on reject.
            </p>
            <ProposalDecisionActions proposalId={proposalId} gate="final" onDecided={onClose} />
          </div>
        ) : proposal?.decisionRef?.decidedAt ? (
          <p className="text-xs text-muted">
            Decided {new Date(proposal.decisionRef.decidedAt).toLocaleString()}
            {proposal.decisionRef.rationale && <> — "{proposal.decisionRef.rationale}"</>}
          </p>
        ) : undefined
      }
    >
      <div className="space-y-6">
        {isLoading && <Skeleton className="h-32 rounded-lg" />}
        {error && <Callout tone="danger">Failed to load the proposal detail.</Callout>}

        {proposal && (
          <>
            {/* Summary */}
            <Section title="Summary">
              <p className="text-sm text-body">{proposal.summary}</p>
              <div className="flex flex-wrap gap-2 mt-2 text-xs text-muted">
                {proposal.confidence && (
                  <span>
                    Confidence: <span className="text-body">{proposal.confidence}</span>
                  </span>
                )}
                {proposal.expectedImpact && (
                  <span>
                    Expected impact: <span className="text-body">{proposal.expectedImpact}</span>
                  </span>
                )}
                {proposal.sourceArm && (
                  <span>
                    Source arm: <span className="text-body">{proposal.sourceArm}</span>
                  </span>
                )}
              </div>
            </Section>

            {/* Evidence refs */}
            {proposal.evidenceRefs.length > 0 && (
              <Section title="Evidence references">
                <ul className="space-y-1">
                  {proposal.evidenceRefs.map((ref, i) => (
                    <li key={`${ref.type}-${ref.id ?? i}`}>
                      <EvidenceRefRow ref={ref} />
                    </li>
                  ))}
                </ul>
              </Section>
            )}

            {/* Acceptance criteria */}
            {proposal.acceptanceCriteria && proposal.acceptanceCriteria.length > 0 && (
              <Section title="Acceptance criteria">
                <div className="divide-y divide-border rounded-lg border bg-surface-alt px-3 py-1">
                  {proposal.acceptanceCriteria.map((criterion) => (
                    <CriterionRow key={criterion.id} criterion={criterion} />
                  ))}
                </div>
              </Section>
            )}

            {/* Implementation refs */}
            {proposal.implementationRefs && (
              <Section title="Implementation">
                <div className="space-y-1 text-xs">
                  {proposal.implementationRefs.prUrl && (
                    <a
                      href={safeHref(proposal.implementationRefs.prUrl) ?? undefined}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center gap-1 text-accent-text hover:underline"
                    >
                      <ExternalLink size={11} /> Pull Request
                    </a>
                  )}
                  {proposal.implementationRefs.templateVersionId && (
                    <p className="text-muted">
                      Template version:{' '}
                      <span className="text-body">{proposal.implementationRefs.templateVersionId}</span>
                    </p>
                  )}
                  {proposal.implementationRefs.catalogCandidateIds &&
                    proposal.implementationRefs.catalogCandidateIds.length > 0 && (
                      <p className="text-muted">
                        Catalog candidates:{' '}
                        <span className="text-body">{proposal.implementationRefs.catalogCandidateIds.join(', ')}</span>
                      </p>
                    )}
                </div>
              </Section>
            )}

            {/* Audit evidence */}
            <Section title="Audit evidence">
              {proposal.auditRefs ? (
                <div className="rounded-lg border bg-surface-alt px-4 py-3 space-y-2 text-xs">
                  {proposal.auditRefs.evidenceHash && (
                    <p className="text-muted break-all">
                      Evidence hash:{' '}
                      <code className="text-body bg-surface px-1 rounded-sm">{proposal.auditRefs.evidenceHash}</code>
                    </p>
                  )}
                  {proposal.auditRefs.evaluationRunIds && proposal.auditRefs.evaluationRunIds.length > 0 && (
                    <p className="text-muted">
                      Evaluation runs:{' '}
                      <span className="text-body">{proposal.auditRefs.evaluationRunIds.join(', ')}</span>
                    </p>
                  )}
                  {proposal.auditRefs.reportIds && proposal.auditRefs.reportIds.length > 0 && (
                    <p className="text-muted">
                      Reports: <span className="text-body">{proposal.auditRefs.reportIds.join(', ')}</span>
                    </p>
                  )}
                  <p className="text-faint mt-1">
                    Full verification table and reviewer findings are in the linked evaluation runs and daemon reports
                    above. Use the Evaluations and Run Health pages to view complete audit detail.
                  </p>
                </div>
              ) : (
                <p className="text-xs text-faint">No audit refs recorded yet.</p>
              )}
            </Section>

            {/* Cost */}
            <Section title="Cost">
              <div className="flex gap-6 text-xs">
                <span className="text-muted">
                  Total: <span className="text-body">{proposal.costUsd != null ? `$${proposal.costUsd}` : '—'}</span>
                </span>
                <span className="text-muted">
                  Paid model:{' '}
                  <span className="text-body">
                    {proposal.paidModelCostUsd != null ? `$${proposal.paidModelCostUsd}` : '—'}
                  </span>
                </span>
                <span className="text-muted">
                  Trials: <span className="text-body">{proposal.trialCount}</span>
                </span>
              </div>
            </Section>

            {/* Lineage */}
            {(proposal.parentProposalId != null || proposal.lineageRootId != null) && (
              <Section title="Lineage">
                <div className="text-xs space-y-1">
                  {proposal.parentProposalId != null && (
                    <p className="text-muted">
                      Parent: <span className="text-body">#{proposal.parentProposalId}</span>
                    </p>
                  )}
                  {proposal.lineageRootId != null && (
                    <p className="text-muted">
                      Root: <span className="text-body">#{proposal.lineageRootId}</span>
                    </p>
                  )}
                </div>
              </Section>
            )}

            {/* Outcome (post-apply) */}
            {proposal.outcome && (
              <Section title="Outcome">
                <div className="text-xs space-y-1">
                  {proposal.outcome.verdict && (
                    <p className="text-muted">
                      Verdict: <span className="text-body">{proposal.outcome.verdict}</span>
                    </p>
                  )}
                  {proposal.outcome.measuredAt && (
                    <p className="text-muted">
                      Measured at:{' '}
                      <span className="text-body">{new Date(proposal.outcome.measuredAt).toLocaleString()}</span>
                    </p>
                  )}
                </div>
              </Section>
            )}

            {/* Stage log */}
            {proposal.stageLog.length > 0 && (
              <Section title="Stage history">
                <StageLogTimeline entries={proposal.stageLog} />
              </Section>
            )}
          </>
        )}
      </div>
    </Sheet>
  );
}
